import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import './StudentDashboard.css';

function ViewContactUs({ studentData, userType = 'student' }) {
  const [contactDetails, setContactDetails] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryForm, setQueryForm] = useState({ subject: '', message: '' });
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (studentData?.school_id) {
      fetchContactDetails();
      fetchFAQs();
    }
  }, [studentData?.school_id]);

  const fetchContactDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('school_contact_details')
        .select('*')
        .eq('school_id', studentData.school_id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching contact details:', error);
      } else {
        setContactDetails(data);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFAQs = async () => {
    try {
      const { data, error } = await supabase
        .from('school_faqs')
        .select('*')
        .eq('school_id', studentData.school_id)
        .order('order_index', { ascending: true });

      if (error) {
        console.error('Error fetching FAQs:', error);
      } else {
        setFaqs(data || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handlePhoneClick = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmailClick = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const submitQuery = async () => {
    if (!queryForm.subject || !queryForm.message) {
      alert('Please fill in both subject and message');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        school_id: studentData.school_id,
        user_type: userType,
        subject: queryForm.subject.trim(),
        message: queryForm.message.trim(),
        status: 'pending',
        created_at: new Date().toISOString()
      };

      if (userType === 'student') {
        payload.student_id = studentData.id;
      } else if (userType === 'parent') {
        payload.parent_id = studentData.id;
      }

      const { error } = await supabase
        .from('support_queries')
        .insert(payload);

      if (error) throw error;

      alert('Your query has been submitted successfully! We will get back to you soon.');
      setQueryForm({ subject: '', message: '' });
      setShowQueryForm(false);
    } catch (error) {
      console.error('Error submitting query:', error);
      alert('Failed to submit query: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="contact-us-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-us-container">
      <div className="contact-us-header">
        <h2>📞 Contact Us</h2>
        <p>Get in touch with our support team</p>
      </div>

      {/* Contact Details */}
      {contactDetails && (
        <div className="contact-details-card">
          <h3>Contact Information</h3>
          <div className="contact-info-list">
            {contactDetails.phone_number && (
              <div className="contact-info-item">
                <span className="contact-label">📞 Phone:</span>
                <a 
                  href={`tel:${contactDetails.phone_number}`}
                  onClick={(e) => { e.preventDefault(); handlePhoneClick(contactDetails.phone_number); }}
                  className="contact-link"
                >
                  {contactDetails.phone_number}
                </a>
              </div>
            )}
            {contactDetails.email && (
              <div className="contact-info-item">
                <span className="contact-label">✉️ Email:</span>
                <a 
                  href={`mailto:${contactDetails.email}`}
                  onClick={(e) => { e.preventDefault(); handleEmailClick(contactDetails.email); }}
                  className="contact-link"
                >
                  {contactDetails.email}
                </a>
              </div>
            )}
            {contactDetails.address && (
              <div className="contact-info-item">
                <span className="contact-label">📍 Address:</span>
                <span className="contact-value">{contactDetails.address}</span>
              </div>
            )}
            {contactDetails.website && (
              <div className="contact-info-item">
                <span className="contact-label">🌐 Website:</span>
                <a 
                  href={contactDetails.website}
                  target="_blank"
                  rel="noreferrer"
                  className="contact-link"
                >
                  {contactDetails.website}
                </a>
              </div>
            )}
            {contactDetails.office_hours && (
              <div className="contact-info-item">
                <span className="contact-label">🕐 Office Hours:</span>
                <span className="contact-value">{contactDetails.office_hours}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQs Section */}
      {faqs.length > 0 && (
        <div className="faqs-section">
          <h3>Frequently Asked Questions</h3>
          <div className="faqs-list">
            {faqs.map((faq, index) => (
              <div key={faq.id} className="faq-card">
                <h4>Q{index + 1}: {faq.question}</h4>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit Query Section */}
      <div className="submit-query-section">
        <h3>Submit a Query</h3>
        <p>Have a question? Send us a message and we'll get back to you as soon as possible.</p>
        <button className="btn-primary" onClick={() => setShowQueryForm(true)}>
          Submit Query
        </button>
      </div>

      {/* Query Form Modal */}
      {showQueryForm && (
        <div className="modal-backdrop" onClick={() => { setShowQueryForm(false); setQueryForm({ subject: '', message: '' }); }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submit Query to Support</h3>
              <button className="close-btn" onClick={() => { setShowQueryForm(false); setQueryForm({ subject: '', message: '' }); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={queryForm.subject}
                  onChange={(e) => setQueryForm({...queryForm, subject: e.target.value})}
                  placeholder="Enter subject"
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  rows="6"
                  value={queryForm.message}
                  onChange={(e) => setQueryForm({...queryForm, message: e.target.value})}
                  placeholder="Enter your message"
                />
              </div>
              <div className="form-actions">
                <button className="btn-primary" onClick={submitQuery} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Query'}
                </button>
                <button className="btn-secondary" onClick={() => { setShowQueryForm(false); setQueryForm({ subject: '', message: '' }); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewContactUs;
